import { faTrash } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Formik } from "formik";
import { useEffect, useState } from "react";
import { Button, Container, Form, Row, Col } from "reactstrap";
import * as Yup from "yup";
import {
  createOrUpdateArticleItem,
  deleteProjectArticleConfiguration,
  getApproversForProject,
  getProjectArticleItems,
} from "../../services/projectService";
import { itemCodeApplicableDays } from "../../utility/constants";
import Message from "../../utility/notifications/Message";
import Notify from "../../utility/notifications/Notify";
import AppCheckboxInput from "../components/forms/AppCheckboxInput";
import AppFormTagBox from "../components/forms/AppFormTagBox";
import AppSelectInput from "../components/forms/AppSelectInput";
import AppTextInput from "../components/forms/AppTextInput";
import CopyConfigurationModal from "./CopyConfigurationModal";
import { useNavigate } from "react-router-dom";

import { SubmitButton } from "../components/buttons/SubmitButton";
import Page from "../components/Page";

const validationSchema = Yup.object().shape({
  items: Yup.array()
    .of(
      Yup.object()
        .shape({
          itemName: Yup.string().required().nullable().label("Itemname"),
          sorting: Yup.number().min(0).nullable().required("Sort order"),
          itemCodesType0: Yup.array().default([]),
          itemCodesType1: Yup.array().default([]),
          itemCodesType2: Yup.array().default([]),
          itemCodesType3: Yup.array().default([]),
          applicableDays: Yup.array().of(Yup.number()).nullable(),
        })
        .test(
          "at-least-one-code",
          "At least one item code must be selected",
          function (item) {
            const total =
              (item.itemCodesType0?.length || 0) +
              (item.itemCodesType1?.length || 0) +
              (item.itemCodesType2?.length || 0) +
              (item.itemCodesType3?.length || 0);

            if (total === 0) {
              return this.createError({
                path: "itemCodesType0", // Or choose the one you want to show the error under
                message: "At least one item code must be selected",
              });
            }

            return true;
          }
        )
    )
    .test(
      "unique-applicableDays",
      "Applicable days overlap or requirements not met",
      function (items) {
        // Filter only active items
        const activeItems = items.filter((item) => item.isActive);

        const itemGroup = activeItems.reduce((acc, item, index) => {
          if (!acc[item.itemName]) {
            acc[item.itemName] = [];
          }
          acc[item.itemName].push({ data: item, index });
          return acc;
        }, {});

        const itemNames = Object.keys(itemGroup);
        for (let name of itemNames) {
          const group = itemGroup[name];
          if (group.length > 1) {
            let daySet = new Set();
            for (let i = 0; i < group.length; i++) {
              const item = group[i].data;
              const currentIndex = group[i].index;
              if (!item.applicableDays || item.applicableDays.length === 0) {
                throw this.createError({
                  path: `items[${currentIndex}].applicableDays`,
                  message:
                    "Applicable days cannot be empty when multiple active items share the same name.",
                });
              }
              const currentDays = new Set(item.applicableDays);
              for (let day of currentDays) {
                if (daySet.has(day)) {
                  throw this.createError({
                    path: `items[${currentIndex}].applicableDays`,
                    message:
                      "Applicable days overlap with another active item having the same name.",
                  });
                }
              }
              item.applicableDays.forEach((day) => daySet.add(day));
            }
          }
        }
        return true;
      }
    )
    .test(
      "unique-sortOrder",
      "Sort order must be unique across all active items",
      function (items) {
        const activeItems = items
          .map((item, index) => ({ ...item, index }))
          .filter((item) => item.isActive);

        const sortOrderSet = new Set();
        for (let i = 0; i < activeItems.length; i++) {
          const sortOrder = activeItems[i].sorting;
          const currentIndex = activeItems[i].index;

          if (sortOrderSet.has(sortOrder)) {
            return this.createError({
              path: `items[${currentIndex}].sorting`,
              message: "Sort order must be unique among active items.",
            });
          }

          sortOrderSet.add(sortOrder);
        }

        return true;
      }
    )
});

const ItemCodeTab = ({ projectId }) => {
  const navigate = useNavigate();

  const [isActiveFilter, setIsActiveFilter] = useState(false);
  const [itemData, setItemData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [projectApprovers, setProjectApprovers] = useState();
  const [showCopyConfigurationModal, setShowCopyConfigurationModal] =
    useState(false);

  async function fetchItems() {
    setLoading(true);
    const response = await getProjectArticleItems(projectId, isActiveFilter);

    const mappedObject = response.data.result.items.map((configItem) => {
      // Convert to { label, value, type } first
      const formattedItemCodes = configItem.itemCodes.map((code) => ({
        label: code.itemCode,
        value: code.id,
        type: code.type,
      }));

      // Split by type
      configItem.itemCodesType0 = formattedItemCodes.filter(
        (code) => code.type === 0
      );
      configItem.itemCodesType1 = formattedItemCodes.filter(
        (code) => code.type === 1
      );
      configItem.itemCodesType2 = formattedItemCodes.filter(
        (code) => code.type === 2
      );
      configItem.itemCodesType3 = formattedItemCodes.filter(
        (code) => code.type === 3
      );

      // Optional: remove original itemCodes if not used
      delete configItem.itemCodes;

      // Sort linked approvers for UI
      configItem.linkedApprovers = configItem.linkedApprovers.sort(
        (a, b) => a.roleId - b.roleId
      );

      return configItem;
    });

    setItemData(mappedObject);
    setLoading(false);
  }

  async function fetchApproversForProject() {
    setLoading(true);
    const response = await getApproversForProject(projectId);

    setProjectApprovers(
      response.data.result.sort((a, b) => a.roleId - b.roleId)
    );

    setLoading(false);
  }

  useEffect(() => {
    fetchItems();
    fetchApproversForProject();
  }, [projectId, isActiveFilter]);

  const deleteRemovedItems = (configurationItems) => {
    itemData.map(async (item) => {
      if (configurationItems.filter((e) => e.id === item.id).length === 0) {
        await deleteProjectArticleConfiguration(item.id);
      }
    });
  };

  const handleSubmit = async (configurationItems) => {
    setSaving(true);

    const mappedObject = configurationItems.items.map((configItem) => {
      // Merge itemCodes from type0 to type3
      const mergedItemCodes = [
        ...(configItem.itemCodesType0 ?? []),
        ...(configItem.itemCodesType1 ?? []),
        ...(configItem.itemCodesType2 ?? []),
        ...(configItem.itemCodesType3 ?? []),
      ];

      configItem.itemCodes = mergedItemCodes.map((code) => ({
        itemCode: code.label,
        id: code.value,
        type: code.type,
      }));

      configItem.approverIds = configItem.linkedApprovers
        .filter((linkedApp) => linkedApp.isChecked)
        .map((linkedApprover) => linkedApprover.roleId);

      delete configItem.linkedApprovers;
      delete configItem.itemCodesType0;
      delete configItem.itemCodesType1;
      delete configItem.itemCodesType2;
      delete configItem.itemCodesType3;

      return configItem;
    });

    deleteRemovedItems(configurationItems.items.filter((e) => e.id));

    await createOrUpdateArticleItem({
      projectId: projectId,
      items: mappedObject,
    })
      .then((res) => {
        Notify.success(`Configuration has been saved`);
        setSaving(false);

        navigate(-1);
      })
      .catch((err) => {
        setSaving(false);
        Message(
          err.response.data.error.message,
          err.response.data.error.details,
          "error"
        );
      });
  };

  const handleDelete = (index, values, setValues) => {
    const filteredItemList = values.items.filter((_, i) => i !== index);
    setValues({
      items: filteredItemList,
    });
  };

  if (!projectId) {
    return (
      <div className="mt-2">
        <h5>You can configure articles after creating the project</h5>
      </div>
    );
  }

  return (
    <Page loading={loading} loadingMessage="Retrieving article configuration">
      <div className="mt-2">
        {!loading && itemData.length === 0 && (
          <h5>
            No article configurations found yet. Click the 'add' button below to
            add a configuration
          </h5>
        )}

        <Row className="pb-1">
          <Col className="m-3 d-flex justify-content-end align-items-center">
            <div className="form-check">
              <input
                type="checkbox"
                className="form-check-input"
                id="isActiveCheckbox"
                checked={isActiveFilter}
                onChange={(e) => setIsActiveFilter(e.target.checked)}
              />
              <label
                htmlFor="isActiveCheckbox"
                className="form-check-label ms-2"
              >
                Show Inactive Records
              </label>
            </div>
          </Col>
        </Row>

        <Formik
          enableReinitialize={true}
          initialValues={{ items: itemData }}
          onSubmit={handleSubmit}
          validationSchema={validationSchema}
        >
          {({ handleSubmit, values, setValues }) => (
            <Form onSubmit={handleSubmit} className="mt-2">
              <Container fluid className="scrollable-container">
                <div className="header-row">
                  <div className="column-actions">Actions</div>
                  <div className="column-item-name">Item name</div>
                  <div className="column-item-name">CO</div>
                  <div className="column-item-name">SO</div>
                  <div className="column-item-name">SOL</div>
                  <div className="column-item-name">Item</div>
                  <div className="column-sort-order">Sort order</div>
                  <div className="column-app-days">Applicable days</div>
                  <div className="column-dont-send">Do not send to AFAS</div>
                  <div className="column-is-active">Is Active</div>
                  {projectApprovers?.map((approver) => (
                    <div className="column-approver" key={approver.roleId}>
                      {approver.roleName}
                    </div>
                  ))}
                </div>

                {values.items?.map((item, index) => (
                  <div className="content-row mb-2" key={index}>
                    <div className="column-actions">
                      <Button
                        onClick={() => handleDelete(index, values, setValues)}
                        size="sm"
                        color="danger"
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </Button>
                    </div>
                    <div className="column-item-name">
                      <AppTextInput
                        name={`items[${index}].itemName`}
                        required
                      />
                    </div>
                    <div className="column-item-name">
                      <AppFormTagBox
                        key={`itemcode-co-key[${index}]`}
                        name={`items[${index}].itemCodesType0`}
                        options={item?.itemCodesType0}
                        additionalData={{ type: 0 }}
                        numbersOnly={true}
                      />
                    </div>
                    <div className="column-item-name">
                      <AppFormTagBox
                        key={`itemcode-so-key[${index}]`}
                        name={`items[${index}].itemCodesType1`}
                        options={item?.itemCodesType1}
                        additionalData={{ type: 1 }}
                        numbersOnly={true}
                      />
                    </div>
                    <div className="column-item-name">
                      <AppFormTagBox
                        key={`itemcode-col-key[${index}]`}
                        name={`items[${index}].itemCodesType2`}
                        options={item?.itemCodesType2}
                        additionalData={{ type: 2 }}
                        numbersOnly={true}
                      />
                    </div>
                    <div className="column-item-name">
                      <AppFormTagBox
                        key={`itemcode-item-key[${index}]`}
                        name={`items[${index}].itemCodesType3`}
                        options={item?.itemCodesType3}
                        additionalData={{ type: 3 }}
                        numbersOnly={true}
                      />
                    </div>
                    <div className="column-sort-order">
                      <AppTextInput
                        type="number"
                        name={`items[${index}].sorting`}
                        required
                      />
                    </div>
                    <div className="column-app-days">
                      <AppSelectInput
                        isMultiple={true}
                        name={`items[${index}].applicableDays`}
                        options={itemCodeApplicableDays}
                      />
                    </div>
                    <div className="column-dont-send">
                      <AppCheckboxInput
                        name={`items[${index}].doNotSendToAFAS`}
                      />
                    </div>
                    <div className="column-is-active">
                      <AppCheckboxInput
                        name={`items[${index}].isActive`}
                        required
                        label={""}
                      />
                    </div>
                    {item.linkedApprovers?.map((approver, approverIndex) => (
                      <div className="column-approver" key={approverIndex}>
                        <AppCheckboxInput
                          name={`items[${index}].linkedApprovers[${approverIndex}].isChecked`}
                        />
                      </div>
                    ))}
                  </div>
                ))}
              </Container>

              <div className="mt-5">
                <Button
                  color="secondary"
                  size="lg"
                  type="button"
                  disabled={saving}
                  onClick={() => {
                    const newItem = {
                      itemName: "",
                      itemCode: 0,
                      linkedApprovers: projectApprovers.map((approver) => {
                        return { isChecked: false, roleId: approver.roleId };
                      }),
                    };

                    setValues({
                      items: [...values.items, newItem],
                    });
                  }}
                >
                  Add configuration
                </Button>
              </div>

              <hr></hr>
              <div className="d-flex justify-content-between">
                <Button
                  color="secondary"
                  size="lg"
                  type="button"
                  className="align-self-start"
                  disabled={saving}
                  onClick={() => {
                    setShowCopyConfigurationModal(true);
                  }}
                >
                  Copy configuration
                </Button>

                <SubmitButton disabled={saving} title="Save configuration" />
              </div>
            </Form>
          )}
        </Formik>
      </div>
      <CopyConfigurationModal
        projectId={projectId}
        setShowModal={setShowCopyConfigurationModal}
        showModal={showCopyConfigurationModal}
        type="ArticleConfig"
        onSavingDone={fetchItems}
      />
    </Page>
  );
};

export default ItemCodeTab;
